<script>
import { toDecPercent, getNumber, toMoney } from '@utils/Format'
import { REPORT_PALETTE } from '@modules/consolidator/constants/Color'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'

export default {
  name: 'LiquidezChart',
  components: { FeedbackWrapper },
  props: {
    liquidezData: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      default: 'Liquidez',
    },
    maxItems: {
      type: Number,
      default: 7,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    minHeight: {
      type: [String, Number],
      default: 385,
    },
    emptyMessage: {
      type: String,
      default: 'Sem dados no momento.',
    },
  },
  computed: {
    processedData() {
      if (!this.liquidezData.length)
        return { labels: [], percents: [], values: [] }

      let items = this.liquidezData.map(({ name, weight, pl }) => ({
        label: name,
        percent: parseFloat(weight.replace(',', '.').replace('%', '')) / 100,
        value: pl,
      }))

      items = items.sort((a, b) => b.percent - a.percent)

      const topItems = items.slice(0, this.maxItems)
      const othersTotalPercent = items
        .slice(this.maxItems)
        .reduce((sum, i) => sum + i.percent, 0)
      const othersTotalValue = toMoney(
        items
          .slice(this.maxItems)
          .reduce((sum, i) => sum + getNumber(i.value), 0)
      )

      if (othersTotalPercent > 0) {
        topItems.push({
          label: 'Outros',
          percent: othersTotalPercent,
          value: othersTotalValue,
        })
      }

      return {
        labels: topItems.map((i) => i.label),
        percents: topItems.map((i) => i.percent),
        values: topItems.map((i) => i.value),
      }
    },
    chartConfig() {
      return {
        data: {
          labels: this.processedData.labels,
          datasets: [
            {
              data: this.processedData.percents.map((p) => p * 100),
              backgroundColor: this.processedData.labels.map((_, i) =>
                this.getColor(i)
              ),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const index = tooltipItem.index
                const label = data.labels[index]
                const percent = data.datasets[0].data[index] / 100
                const value = this.processedData.values[index]
                return `${label} (${toDecPercent(percent)}): ${value}`
              },
            },
          },
          legend: {
            display: false,
          },
        },
      }
    },
    legends() {
      return this.processedData.labels.map((label, index) => ({
        label,
        percent: this.processedData.percents[index],
        color: this.getColor(index),
        value: this.processedData.values[index],
      }))
    },
    isSmall() {
      if (this.$vuetify.breakpoint) {
        return this.$vuetify.breakpoint.mdAndDown
      } else {
        return false
      }
    },
  },
  methods: {
    getColor(index) {
      return REPORT_PALETTE[index % REPORT_PALETTE.length] || 'var(--neutral-300)'
    },
    formatTooltip(legend) {
      return `${legend.label} (${toDecPercent(legend.percent)}): ${
        legend.value
      }`
    },
  },
}
</script>

<template>
  <div
    class="liquidez-chart az-card-bordered"
    :class="{ 'liquidez-chart--loading': loading }"
  >
    <div v-if="!loading" class="liquidez-chart__title az-ds-card-title">
      {{ title }}
    </div>
    <FeedbackWrapper
      :loading="loading"
      :noData="!processedData.labels.length"
      :message="emptyMessage"
      :minHeight="minHeight"
    >
      <div class="liquidez-chart__content">
        <div class="liquidez-chart__graph">
          <donut-chart
            :chartData="chartConfig.data"
            :options="chartConfig.options"
            :width="isSmall ? 150 : 375"
            :height="isSmall ? 150 : 250"
          />
        </div>
        <div class="liquidez-chart__legends">
          <div
            v-for="(legend, index) in legends"
            :key="index"
            class="liquidez-chart__legend"
          >
            <div
              class="liquidez-chart__legend-color"
              :style="{ backgroundColor: legend.color }"
            ></div>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span class="liquidez-chart__legend-label" v-on="on">
                  {{ legend.label }}
                </span>
              </template>
              <span>{{ formatTooltip(legend) }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass">
.liquidez-chart
  height: 100%
  padding-bottom: 32px

  &--loading
    padding: 0 !important

    .feedback-wrapper > div
      height: 100% !important
      width: 100% !important

  &__title
    margin-bottom: 56px

  &__content
    display: flex
    align-items: center
    justify-content: center

  &__graph
    min-width: 250px

  &__legend
    display: flex
    align-items: center
    margin-bottom: 8px

  &__legend-color
    width: 21px
    height: 8px
    margin-right: 8px
    flex-shrink: 0

  &__legend-label
    font-size: 0.75rem
    color: var(--neutral-700)
    text-overflow: ellipsis
    white-space: nowrap
    overflow: hidden
    max-width: 150px
    display: block
</style>
